/**
 * AskExpertModal — inline "Ask an Expert" flow.
 *
 * A member types a question and instantly gets an AI answer from
 * Prof. FreedrIQ (Claude Haiku 4.5). If the answer isn't enough, they can
 * one-click DM a currently-online human expert — the DM is pre-filled
 * with the original question and the AI's reply so the expert has full
 * context on their first click.
 *
 * Props:
 *   open, onClose
 *   context: { type?, id?, title?, snippet? }   // optional — sharpens the AI answer
 */
import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Sparkles, X, MessageCircle, UserCircle2, Loader2 } from 'lucide-react';

const AskExpertModal = ({ open, onClose, context }) => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState('');
  const [questionId, setQuestionId] = useState(null);
  const [experts, setExperts] = useState([]);
  const [escalating, setEscalating] = useState(false);

  if (!open) return null;

  const reset = () => {
    setQuestion(''); setAnswer(''); setQuestionId(null); setExperts([]);
  };
  const close = () => { reset(); onClose && onClose(); };

  const submit = async () => {
    const q = question.trim();
    if (q.length < 3) {
      toast.error('Please write a longer question.');
      return;
    }
    setAsking(true);
    try {
      const body = { question: q };
      if (context?.type)    body.context_type    = String(context.type);
      if (context?.id)      body.context_id      = String(context.id);
      if (context?.title)   body.context_title   = String(context.title).slice(0, 240);
      if (context?.snippet) body.context_snippet = String(context.snippet).slice(0, 1200);
      const r = await axios.post(`${API}/ask-expert`, body);
      setAnswer(r.data.answer || '');
      setQuestionId(r.data.question_id);
      setExperts(r.data.online_experts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reach the AI. Try again.');
    } finally {
      setAsking(false);
    }
  };

  const escalate = async (expertId) => {
    if (!questionId) return;
    setEscalating(true);
    try {
      const r = await axios.post(`${API}/ask-expert/${questionId}/escalate`, { expert_id: expertId });
      toast.success('Expert pinged — jumping to your DM.');
      close();
      navigate('/messages');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open DM.');
    } finally {
      setEscalating(false);
    }
  };

  return createPortal((
    <div
      className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
      data-testid="ask-expert-modal"
      onClick={close}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-2xl p-5 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <div className="h-9 w-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Ask an Expert</div>
            <h3 className="font-heading text-base font-extrabold text-foreground">
              Get a straight answer.
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Prof. FreedrIQ answers instantly. If you want a human take, ping one of the
              experts currently online.
            </p>
          </div>
          <button
            type="button" onClick={close}
            className="ml-auto h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
            aria-label="Close" data-testid="ask-expert-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {context?.title && (
          <div className="mt-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">In:</span> {context.title}
          </div>
        )}

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground" htmlFor="ask-expert-q">
            Your question
          </label>
          <textarea
            id="ask-expert-q"
            rows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. What shelf temperature should I use for freeze-drying strawberries?"
            data-testid="ask-expert-question-input"
            disabled={asking || !!answer}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 disabled:opacity-70"
          />
        </div>

        {!answer && (
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={close} disabled={asking} className="rounded-full font-bold">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={asking || question.trim().length < 3}
              className="rounded-full font-bold"
              data-testid="ask-expert-submit-btn"
            >
              {asking ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Asking…</>) : 'Get instant answer'}
            </Button>
          </div>
        )}

        {answer && (
          <>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Instant answer
              </div>
              <div
                className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-foreground whitespace-pre-wrap leading-relaxed"
                data-testid="ask-expert-answer"
              >
                {answer}
              </div>
            </div>

            <div className="mt-4 border-t border-border pt-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                Want a human take?
              </div>
              {experts.length === 0 ? (
                <div className="text-xs text-muted-foreground">
                  No live experts online right now. Your question is saved — a moderator
                  will follow up if it looks tricky.
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="ask-expert-online-experts">
                  {experts.map((x) => (
                    <div
                      key={x.user_id}
                      className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-1.5"
                    >
                      <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                        {x.avatar_url ? (
                          <img src={x.avatar_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <UserCircle2 className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-foreground truncate">{x.display_name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">
                          Online • {x.role || 'expert'}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => escalate(x.user_id)}
                        disabled={escalating}
                        className="rounded-full font-bold"
                        data-testid={`ask-expert-ping-${x.user_id}`}
                      >
                        <MessageCircle className="w-3 h-3 mr-1" /> Message
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button size="sm" variant="outline" onClick={reset} className="rounded-full font-bold" data-testid="ask-expert-reset-btn">
                Ask another
              </Button>
              <Button size="sm" onClick={close} className="rounded-full font-bold">
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  ), document.body);
};

export default AskExpertModal;
